import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Logo } from '../../components/ui/Logo';

export default function ForgotPasswordPage() {
  const { forgotPassword, resetUserPassword } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setStep('reset');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al enviar el código');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetUserPassword(email, code, newPassword);
      navigate('/login');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Código inválido o contraseña incorrecta');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{ background: 'radial-gradient(ellipse at 60% 20%, #2d1b69 0%, #0f172a 70%)' }}
    >
      {/* Floating decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        {['🔑', '⚽', '🏆', '⭐', '🌍', '🎯'].map((emoji, i) => (
          <span
            key={i}
            className="absolute text-3xl opacity-10"
            style={{
              top:       `${[15, 72, 35, 58, 20, 80][i]}%`,
              left:      `${[8,  88, 72, 15, 45, 60][i]}%`,
              transform: `rotate(${[-12, 8, -20, 15, -5, 18][i]}deg)`,
            }}
          >
            {emoji}
          </span>
        ))}
      </div>

      <div className="relative w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-brand-600 to-brand-700 px-8 py-7 flex flex-col items-center gap-1">
            <Logo size="md" />
            <p className="text-white/60 text-xs font-body mt-1">
              {step === 'request' ? 'Recupera tu acceso' : 'Crea una nueva contraseña'}
            </p>
          </div>

          {/* Form */}
          <div className="px-8 py-7">
            {step === 'request' ? (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">¿Olvidaste tu contraseña?</h2>
                <p className="text-sm text-gray-500 mb-5">
                  Te enviaremos un código a tu correo para que puedas crear una nueva contraseña.
                </p>
                <form onSubmit={handleRequest} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      Correo electrónico
                    </label>
                    <input
                      type="email"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="input-field"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-semibold">
                      {error}
                    </div>
                  )}

                  <Button type="submit" loading={loading} size="lg" className="w-full mt-1">
                    Enviar código
                  </Button>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Nueva contraseña</h2>
                <p className="text-sm text-gray-500 mb-5">
                  Enviamos un código a <span className="font-bold text-gray-700">{email}</span>
                </p>
                <form onSubmit={handleReset} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      Código de verificación
                    </label>
                    <input
                      type="text"
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      className="input-field text-center text-2xl tracking-[0.5em] font-bold"
                      maxLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      Nueva contraseña
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Mín. 8 caracteres"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        className="input-field pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-semibold">
                      {error}
                    </div>
                  )}

                  <Button type="submit" loading={loading} size="lg" className="w-full mt-1">
                    Cambiar contraseña
                  </Button>
                </form>
              </>
            )}

            <p className="mt-5 text-sm text-center text-gray-400">
              <Link to="/login" className="text-brand-500 font-bold hover:text-brand-600">
                Volver al inicio de sesión
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
